import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Card, PageShell } from '../components/ui';

/**
 * General Terms of Sale (CGV) page.
 * Required by the French Consumer Code (Code de la consommation).
 * Finalized March 2026.
 */
export default function CGVPage() {
  const { t } = useTranslation();

  return (
    <PageShell maxWidth="3xl" mainClassName="py-10">
      <h1 className="mb-8 text-3xl font-bold text-slate-900 dark:text-white">{t('legal.cgv.title')}</h1>

      <Card className="prose max-w-none space-y-6 p-8 text-sm leading-relaxed text-slate-600 dark:prose-invert dark:text-slate-300">
        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('legal.cgv.effectiveDate')}</p>
        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.cgv.section1.title')}</h2>
          <p>{t('legal.cgv.section1.body')}</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.cgv.section2.title')}</h2>
          <p>{t('legal.cgv.section2.intro')}</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>{t('legal.cgv.section2.items.questionBank')}</li>
            <li>{t('legal.cgv.section2.items.examSim')}</li>
            <li>{t('legal.cgv.section2.items.tracking')}</li>
            <li>{t('legal.cgv.section2.items.study')}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.cgv.section3.title')}</h2>
          <p>
            <Trans
              i18nKey="legal.cgv.section3.body"
              components={{
                link: <Link to="/pricing" className="text-blue-600 hover:underline dark:text-blue-400" />,
              }}
            />
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.cgv.section4.title')}</h2>
          <p>
            <Trans i18nKey="legal.cgv.section4.p1" components={{ strong: <strong /> }} />
          </p>
          <p>
            <Trans
              i18nKey="legal.cgv.section4.p2"
              components={{
                link: <Link to="/billing" className="text-blue-600 hover:underline dark:text-blue-400" />,
              }}
            />
          </p>
          <p>
            <Trans i18nKey="legal.cgv.section4.p3" components={{ strong: <strong /> }} />
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.cgv.section5.title')}</h2>
          <p>{t('legal.cgv.section5.body')}</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.cgv.section6.title')}</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <Trans i18nKey="legal.cgv.section6.items.withdrawal" components={{ strong: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="legal.cgv.section6.items.afterWithdrawal" components={{ strong: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="legal.cgv.section6.items.technical" components={{ strong: <strong /> }} />
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.cgv.section7.title')}</h2>
          <p>{t('legal.cgv.section7.p1')}</p>
          <p>
            <Trans
              i18nKey="legal.cgv.section7.p2"
              components={{
                emailLink: <a href="mailto:contact@cloudmaster.app" className="text-blue-600 hover:underline dark:text-blue-400" />,
                odrLink: (
                  <a
                    href="https://ec.europa.eu/consumers/odr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline dark:text-blue-400"
                  />
                ),
              }}
            />
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.cgv.section8.title')}</h2>
          <p>{t('legal.cgv.section8.body')}</p>
        </section>

        <p className="border-t border-slate-200 pt-4 text-xs text-slate-400 dark:border-white/10 dark:text-slate-500">
          {t('legal.cgv.lastUpdated')}
        </p>
      </Card>
    </PageShell>
  );
}
